import type { EntityKind, ParkState, RotationQuarter, SubcellAnchor } from '../types';
import { evaluateOccupancy, occupiedSubcellsForKind } from './placement';
import { isUnlockedPaintTarget, type FloorPaintStyle } from './floors';

export type PlacementPreviewMode = 'move' | 'build';

export interface PlacementPreview {
  mode: PlacementPreviewMode;
  kind: EntityKind;
  entityId?: string;
  candidateAnchor: SubcellAnchor;
  candidateRotation: RotationQuarter;
  occupied: SubcellAnchor[];
  valid: boolean;
  reason?: 'occupied' | 'locked';
}

export interface FloorBrushPreview {
  mode: 'floor';
  coord: SubcellAnchor;
  style: FloorPaintStyle;
  valid: boolean;
  reason?: 'locked';
}

export function nextPreviewRotation(rotation: RotationQuarter): RotationQuarter {
  return ((rotation + 90) % 360) as RotationQuarter;
}

export function evaluatePlacementPreview(
  state: ParkState,
  input: {
    mode: PlacementPreviewMode;
    kind: EntityKind;
    entityId?: string;
    anchor: SubcellAnchor;
    rotation: RotationQuarter;
  },
): PlacementPreview {
  const occupied = occupiedSubcellsForKind(input.kind, input.anchor, input.rotation);
  const occupancy = evaluateOccupancy(state, input.kind, input.anchor, input.rotation, input.entityId);
  return {
    mode: input.mode,
    kind: input.kind,
    entityId: input.entityId,
    candidateAnchor: { sx: input.anchor.sx, sz: input.anchor.sz },
    candidateRotation: input.rotation,
    occupied,
    valid: occupancy.ok,
    reason: occupancy.ok ? undefined : occupancy.reason,
  };
}

export function evaluateFloorBrushPreview(
  state: ParkState,
  coord: SubcellAnchor,
  style: FloorPaintStyle,
): FloorBrushPreview {
  const unlocked = isUnlockedPaintTarget(state, coord);
  return {
    mode: 'floor',
    coord: { sx: coord.sx, sz: coord.sz },
    style,
    valid: unlocked,
    reason: unlocked ? undefined : 'locked',
  };
}

/** Preview is ephemeral. This helper documents that it must never be persisted. */
export function placementPreviewPersistable(_preview: PlacementPreview | FloorBrushPreview | null): false {
  return false;
}
